/**
 * WP10a / #46640 — root + styleguide redirect cluster.
 * WP10c / #46642 — legacy review / verdict / evidence redirect cluster.
 *
 * `/` is the canonical production verification shell (overview, or the
 * environment chooser when no Demo/Real preference is set). It is NOT a
 * redirect in this cluster.
 *
 * `/styleguide/verification` (exact path only) hops to `/` and forwards the
 * incoming query — bookmarks with `?env=demo` / `?q=` must not drop params.
 * Deeper `/styleguide/verification/*` pages stay a marked non-product design
 * archive (splat route; WP10a). Old product lives under `/classic` (WP11a) and
 * is not deleted here.
 *
 * WP10c maps the pre-cutover decision door and its aliases onto the promoted IA:
 *   `/agents/verification`           → overview (`/`)
 *   `/agents/verification/:id`       → `/runs|:findings|:receipts|:evidence/:id` when resolvable
 *   legacy queue aliases             → Decide `/queue`
 *   verdict aliases                  → Findings `/findings` (`/settings/agents/verdicts` stays settings)
 *   receipt / evidence aliases       → Prove `/receipts` or Evidence `/evidence`
 *
 * WP10b / #46641 maps settings / help / database aliases onto retained utility
 * surfaces (HG3/HG4). Canonical `/settings`, `/help`, `/database` are not redirects.
 * Query is forwarded; dest literals win collisions.
 *
 * WP10d / #46643 — unknown old object ids (unresolvable /agents/verification/:id
 * and /verification/:id) hop to a searchable not-found recovery page, not a blank
 * SPA miss. Query is forwarded; recovery sets `q` to the unknown id (dest wins).
 *
 * Pure string → string. No DB, no router, no React.
 */

export type ClusterRedirect = {
  source: string;
  destination: string;
  /** Forward incoming search onto destination; dest literals win collisions. */
  preserveQuery: true;
};

export const CANONICAL_VERIFICATION_ROOT = "/";
export const STYLEGUIDE_VERIFICATION_EXACT = "/styleguide/verification";
export const LEGACY_REVIEW_DOOR = "/agents/verification";
export const LEGACY_REVIEW_DOOR_SHORT = "/verification";
export const LEGACY_VERDICTS = "/verdicts";
export const DECIDE_QUEUE = "/queue";
export const DECIDE_FINDINGS = "/findings";
export const PROVE_RECEIPTS = "/receipts";
export const EVIDENCE_LIBRARY = "/evidence";
export const RETAINED_SETTINGS = "/settings";
export const RETAINED_HELP = "/help";
export const RETAINED_DATABASE = "/database";
export const RETAINED_SETTINGS_USER_PROFILE = "/settings/user/profile";
export const RETAINED_SETTINGS_USER_PREFERENCES = "/settings/user/preferences";
export const RETAINED_SETTINGS_ADVANCED_MCP = "/settings/advanced/mcp";
export const RETAINED_DATABASE_SQL = "/database/sql";
export const UNKNOWN_OLD_ID_RECOVERY = "/not-found";

/** This cluster's table. First-match-wins; do not sort. Exact paths only. */
export const ROOT_STYLEGUIDE_REDIRECTS: readonly ClusterRedirect[] = [
  {
    source: STYLEGUIDE_VERIFICATION_EXACT,
    destination: CANONICAL_VERIFICATION_ROOT,
    preserveQuery: true,
  },
];

/**
 * WP10c exact-path hops (query-tab and `:id` resolution are sibling rules in
 * `resolveReviewVerdictEvidenceRedirect`, not this table).
 */
export const REVIEW_VERDICT_EVIDENCE_REDIRECTS: readonly ClusterRedirect[] = [
  {
    source: LEGACY_REVIEW_DOOR,
    destination: CANONICAL_VERIFICATION_ROOT,
    preserveQuery: true,
  },
  {
    source: LEGACY_REVIEW_DOOR_SHORT,
    destination: CANONICAL_VERIFICATION_ROOT,
    preserveQuery: true,
  },
  {
    source: LEGACY_VERDICTS,
    destination: DECIDE_FINDINGS,
    preserveQuery: true,
  },
  {
    source: "/work/reviews",
    destination: DECIDE_QUEUE,
    preserveQuery: true,
  },
  {
    source: "/work/issues/reviews",
    destination: DECIDE_QUEUE,
    preserveQuery: true,
  },
];

/**
 * WP10b / #46641 — settings / help / database utility aliases.
 * Exact paths only. First-match-wins; do not sort.
 * Canonical retained surfaces (`/settings`, `/help`, `/database`) are not in this
 * table. Live leaves (`/help/settings`, `/settings/agents/database`,
 * `/settings/integrations/database`, `/database/sql`, `/help/$…`) stay put.
 * `/settings/customers` is already a settings-redirects stub (WP RO-3-09).
 */
export const SETTINGS_HELP_DATABASE_REDIRECTS: readonly ClusterRedirect[] = [
  // Settings aliases → retained settings (HG4 User / Advanced / landing).
  {
    source: "/settings/personal",
    destination: RETAINED_SETTINGS_USER_PROFILE,
    preserveQuery: true,
  },
  {
    source: "/settings/apps",
    destination: RETAINED_SETTINGS,
    preserveQuery: true,
  },
  {
    source: "/settings/advanced",
    destination: RETAINED_SETTINGS_ADVANCED_MCP,
    preserveQuery: true,
  },
  {
    source: "/settings/help",
    destination: RETAINED_HELP,
    preserveQuery: true,
  },
  {
    source: "/settings/database",
    destination: RETAINED_DATABASE,
    preserveQuery: true,
  },
  {
    source: "/preferences",
    destination: RETAINED_SETTINGS_USER_PREFERENCES,
    preserveQuery: true,
  },
  {
    source: "/profile",
    destination: RETAINED_SETTINGS_USER_PROFILE,
    preserveQuery: true,
  },
  {
    source: "/account",
    destination: RETAINED_SETTINGS_USER_PROFILE,
    preserveQuery: true,
  },
  // Help aliases → retained help center.
  {
    source: "/docs",
    destination: RETAINED_HELP,
    preserveQuery: true,
  },
  {
    source: "/documentation",
    destination: RETAINED_HELP,
    preserveQuery: true,
  },
  {
    source: "/help-center",
    destination: RETAINED_HELP,
    preserveQuery: true,
  },
  // Database aliases → retained database utility.
  {
    source: "/db",
    destination: RETAINED_DATABASE,
    preserveQuery: true,
  },
  {
    source: "/sql",
    destination: RETAINED_DATABASE_SQL,
    preserveQuery: true,
  },
];

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed.length === 0 ? "/" : trimmed;
}

/**
 * Forward the incoming query onto the destination. The destination's own
 * literal query wins a key collision, matching `resolveConfigRedirect`.
 *
 * @param search `location.searchStr` — INCLUDING its leading `?` when present.
 * @param drop keys consumed by this hop (e.g. `tab=receipts` must not ride onto `/receipts`).
 */
function mergeQuery(destination: string, search: string, drop: readonly string[] = []): string {
  const incomingRaw = search.startsWith("?") ? search.slice(1) : search;
  if (!incomingRaw && drop.length === 0) return destination;

  const [path = "", destQuery = ""] = destination.split("?");
  const merged = new URLSearchParams(destQuery);
  for (const [key, value] of new URLSearchParams(incomingRaw)) {
    if (drop.includes(key)) continue;
    if (!merged.has(key)) merged.append(key, value);
  }
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
}

function paramsOf(search: string): URLSearchParams {
  const incomingRaw = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(incomingRaw);
}

/**
 * Resolve this cluster, or `null` when no rule matches. `/` never redirects.
 * Trailing slashes on the exact styleguide path still match.
 */
export function resolveRootStyleguideRedirect(
  pathname: string,
  search: string = "",
): string | null {
  const normalized = normalizePathname(pathname);
  if (normalized === CANONICAL_VERIFICATION_ROOT) return null;

  for (const rule of ROOT_STYLEGUIDE_REDIRECTS) {
    if (normalized !== rule.source) continue;
    const destination = rule.preserveQuery
      ? mergeQuery(rule.destination, search)
      : rule.destination;
    if (destination === pathname + search) return null;
    return destination;
  }
  return null;
}

export function isCanonicalVerificationRoot(pathname: string): boolean {
  return normalizePathname(pathname) === CANONICAL_VERIFICATION_ROOT;
}

/**
 * Prefix → IA family for `/agents/verification/:id` (and `/verification/:id`).
 * Unprefixed SKU A task ids resolve to runs — that door was the task evidence
 * surface. Empty id is not resolvable (WP10d owns unknown recovery).
 */
/** Family for a legacy review-door id, or null when WP10d must recover it. */
function familyForLegacyVerificationId(id: string): string | null {
  const trimmed = id.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("finding_") || lower.startsWith("fin_") || lower.startsWith("find_")) {
    return "findings";
  }
  if (lower.startsWith("receipt_") || lower.startsWith("rcpt_")) {
    return "receipts";
  }
  if (lower.startsWith("pack_")) {
    return "packs";
  }
  if (lower.startsWith("artifact_") || lower.startsWith("evidence_")) {
    return "evidence";
  }
  if (lower.startsWith("run_")) {
    return "runs";
  }
  if (/^\d+$/.test(trimmed)) {
    return "runs";
  }
  return null;
}

/** True when WP10c can map the id onto a family path (prefix or SKU A digits). */
export function isResolvableLegacyVerificationId(id: string): boolean {
  return familyForLegacyVerificationId(id) !== null;
}

export function resolveLegacyVerificationId(id: string): string | null {
  const trimmed = id.trim();
  const family = familyForLegacyVerificationId(trimmed);
  if (!family) return null;
  return `/${family}/${trimmed}`;
}

function isWp10aOrArchive(normalized: string): boolean {
  if (normalized === CANONICAL_VERIFICATION_ROOT) return true;
  if (normalized === STYLEGUIDE_VERIFICATION_EXACT) return true;
  if (normalized.startsWith("/styleguide/verification/")) return true;
  if (normalized === "/classic" || normalized.startsWith("/classic/")) return true;
  if (normalized === "/settings/agents/verdicts") return true;
  if (
    normalized === "/agents/verification-ops" ||
    normalized.startsWith("/agents/verification-ops/")
  ) {
    return true;
  }
  return false;
}

/**
 * WP10c cluster. Returns null when this slice does not own the path (WP10a
 * styleguide, `/classic`, settings verdicts, verification-ops).
 */
export function resolveReviewVerdictEvidenceRedirect(
  pathname: string,
  search: string = "",
): string | null {
  const normalized = normalizePathname(pathname);
  if (isWp10aOrArchive(normalized)) return null;

  const q = paramsOf(search);
  const tab = q.get("tab");
  const view = q.get("view");
  const area = q.get("area");
  const sub = q.get("sub");

  const isReviewDoor = normalized === LEGACY_REVIEW_DOOR || normalized === LEGACY_REVIEW_DOOR_SHORT;

  if (isReviewDoor && tab === "receipts") {
    return mergeQuery(PROVE_RECEIPTS, search, ["tab"]);
  }
  if (isReviewDoor && tab === "rejects") {
    return mergeQuery(DECIDE_QUEUE, search, ["tab"]);
  }
  if (isReviewDoor && tab === "evidence") {
    return mergeQuery(EVIDENCE_LIBRARY, search, ["tab"]);
  }

  // Review-queue query aliases (catch before the mode cascade in __root).
  const reviewQueueQuery = view === "review" || area === "reviews" || sub === "reviews";
  if (reviewQueueQuery) {
    if (
      normalized === "/issues" ||
      normalized === "/tasks" ||
      normalized === LEGACY_VERDICTS ||
      normalized === "/work" ||
      normalized === "/work/issues"
    ) {
      // Old-product `/work*` env is not verification Demo/Real; do not forward it.
      const drop =
        normalized === "/work" || normalized === "/work/issues"
          ? (["view", "area", "sub", "env"] as const)
          : (["view", "area", "sub"] as const);
      return mergeQuery(DECIDE_QUEUE, search, drop);
    }
  }

  const reviewDoorPrefix = `${LEGACY_REVIEW_DOOR}/`;
  const shortDoorPrefix = `${LEGACY_REVIEW_DOOR_SHORT}/`;
  if (normalized.startsWith(reviewDoorPrefix) || normalized.startsWith(shortDoorPrefix)) {
    const rest = normalized.startsWith(reviewDoorPrefix)
      ? normalized.slice(reviewDoorPrefix.length)
      : normalized.slice(shortDoorPrefix.length);
    const id = rest.split("/").filter(Boolean)[0] ?? "";
    const dest = resolveLegacyVerificationId(id);
    if (!dest) return null;
    return mergeQuery(dest, search);
  }

  for (const rule of REVIEW_VERDICT_EVIDENCE_REDIRECTS) {
    if (normalized !== rule.source) continue;
    // `/work/reviews` and `/work/issues/reviews` are old-product paths; their
    // `env` is not the verification Demo/Real selector.
    const drop = rule.source.startsWith("/work") ? (["env"] as const) : [];
    const destination = rule.preserveQuery
      ? mergeQuery(rule.destination, search, drop)
      : rule.destination;
    if (destination === pathname + search) return null;
    return destination;
  }
  return null;
}

function isWp10aWp10cOrRetainedUtility(normalized: string): boolean {
  if (isWp10aOrArchive(normalized)) return true;
  if (normalized === RETAINED_SETTINGS) return true;
  if (normalized === RETAINED_HELP) return true;
  if (normalized === RETAINED_DATABASE) return true;
  if (normalized === "/help/settings" || normalized.startsWith("/help/")) return true;
  if (normalized === "/settings/agents/database") return true;
  if (normalized === "/settings/integrations/database") return true;
  if (normalized.startsWith("/database/")) return true;
  return false;
}

/**
 * WP10b cluster. Returns null when this slice does not own the path (canonical
 * retained utilities, live help/database leaves, WP10a styleguide, WP10c review
 * door, `/classic`). Unknown ids are not resolved here (WP10d).
 */
export function resolveSettingsHelpDatabaseRedirect(
  pathname: string,
  search: string = "",
): string | null {
  const normalized = normalizePathname(pathname);
  if (isWp10aWp10cOrRetainedUtility(normalized)) return null;

  for (const rule of SETTINGS_HELP_DATABASE_REDIRECTS) {
    if (normalized !== rule.source) continue;
    const destination = rule.preserveQuery
      ? mergeQuery(rule.destination, search)
      : rule.destination;
    if (destination === pathname + search) return null;
    return destination;
  }
  return null;
}

function isRecoveryPath(normalized: string): boolean {
  return (
    normalized === UNKNOWN_OLD_ID_RECOVERY || normalized.startsWith(`${UNKNOWN_OLD_ID_RECOVERY}/`)
  );
}

/** `/not-found?q=<id>` with incoming query merged; dest `q` wins collisions. */
export function unknownOldIdRecoveryPath(id: string, search: string = ""): string {
  const trimmed = id.trim();
  const destination = trimmed
    ? `${UNKNOWN_OLD_ID_RECOVERY}?q=${encodeURIComponent(trimmed)}`
    : UNKNOWN_OLD_ID_RECOVERY;
  return mergeQuery(destination, search);
}

/**
 * WP10d cluster. Returns a searchable not-found hop for unresolvable old
 * object ids on the review-door prefixes. Returns null when WP10a/b/c or a
 * canonical/archive path owns the URL, or when the id is resolvable (WP10c).
 */
export function resolveUnknownOldIdRedirect(pathname: string, search: string = ""): string | null {
  const normalized = normalizePathname(pathname);
  if (isWp10aWp10cOrRetainedUtility(normalized)) return null;
  if (isRecoveryPath(normalized)) return null;

  const reviewDoorPrefix = `${LEGACY_REVIEW_DOOR}/`;
  const shortDoorPrefix = `${LEGACY_REVIEW_DOOR_SHORT}/`;
  if (!(normalized.startsWith(reviewDoorPrefix) || normalized.startsWith(shortDoorPrefix))) {
    return null;
  }
  const rest = normalized.startsWith(reviewDoorPrefix)
    ? normalized.slice(reviewDoorPrefix.length)
    : normalized.slice(shortDoorPrefix.length);
  const id = rest.split("/").filter(Boolean)[0] ?? "";
  if (resolveLegacyVerificationId(id)) return null;
  const destination = unknownOldIdRecoveryPath(id, search);
  if (destination === pathname + search) return null;
  return destination;
}

/**
 * HG10 / #46594 — Matt: copy the old product to ~/Sites/excellent-classic,
 * then deprecate old-product routes in this tree immediately. No calendar
 * sunset. `/work` and `/work/foo` (but not `/workflows`), `/customers`, and
 * `/classic` hop to `/`. More specific WP10c hops (`/work/reviews` → `/queue`)
 * must run first.
 */
export const DEPRECATED_OLD_PRODUCT_PREFIXES = ["/work", "/customers", "/classic"] as const;

export type LegacyRouteCutoverRedirect = {
  source: string;
  destination: string;
  resolver: "resolveReviewVerdictEvidenceRedirect" | "resolveDeprecatedOldProductRedirect";
  telemetryEvent: "verification.legacy_route.redirected";
  evidence: readonly ("E2E-049" | "UI-REALITY-003" | "UI-REALITY-008")[];
};

export type LegacyRouteMigrationTelemetry = {
  event: "verification.legacy_route.redirected" | "verification.legacy_route.unresolved";
  requiredProperties: readonly string[];
};

export type LegacyRouteRemovalStep = {
  phase: "review-safe" | "post-cutover-delete";
  condition: string;
  routes: readonly string[];
};

export const LEGACY_ROUTE_CUTOVER_REDIRECT_MAP: readonly LegacyRouteCutoverRedirect[] = [
  {
    source: "/work",
    destination: CANONICAL_VERIFICATION_ROOT,
    resolver: "resolveDeprecatedOldProductRedirect",
    telemetryEvent: "verification.legacy_route.redirected",
    evidence: ["E2E-049", "UI-REALITY-003"],
  },
  {
    source: "/work/:path*",
    destination: CANONICAL_VERIFICATION_ROOT,
    resolver: "resolveDeprecatedOldProductRedirect",
    telemetryEvent: "verification.legacy_route.redirected",
    evidence: ["E2E-049", "UI-REALITY-003"],
  },
  {
    source: "/work/reviews",
    destination: DECIDE_QUEUE,
    resolver: "resolveReviewVerdictEvidenceRedirect",
    telemetryEvent: "verification.legacy_route.redirected",
    evidence: ["E2E-049", "UI-REALITY-008"],
  },
  {
    source: "/work/issues/reviews",
    destination: DECIDE_QUEUE,
    resolver: "resolveReviewVerdictEvidenceRedirect",
    telemetryEvent: "verification.legacy_route.redirected",
    evidence: ["E2E-049", "UI-REALITY-008"],
  },
  {
    source: "/customers",
    destination: CANONICAL_VERIFICATION_ROOT,
    resolver: "resolveDeprecatedOldProductRedirect",
    telemetryEvent: "verification.legacy_route.redirected",
    evidence: ["E2E-049", "UI-REALITY-003"],
  },
  {
    source: "/classic",
    destination: CANONICAL_VERIFICATION_ROOT,
    resolver: "resolveDeprecatedOldProductRedirect",
    telemetryEvent: "verification.legacy_route.redirected",
    evidence: ["E2E-049", "UI-REALITY-003"],
  },
] as const;

export const LEGACY_ROUTE_MIGRATION_TELEMETRY: readonly LegacyRouteMigrationTelemetry[] = [
  {
    event: "verification.legacy_route.redirected",
    requiredProperties: [
      "source_path",
      "destination_path",
      "resolver",
      "preserved_query",
      "task_id",
      "evidence_ids",
    ],
  },
  {
    event: "verification.legacy_route.unresolved",
    requiredProperties: ["source_path", "recovery_path", "resolver", "task_id", "legacy_object_id"],
  },
] as const;

export const LEGACY_ROUTE_REMOVAL_SCHEDULE: readonly LegacyRouteRemovalStep[] = [
  {
    phase: "review-safe",
    condition: "Keep redirect helpers and telemetry contracts while T46808-05 is in review.",
    routes: ["/work", "/work/:path*", "/customers", "/classic"],
  },
  {
    phase: "post-cutover-delete",
    condition:
      "Remove compatibility routes only after E2E-049, UI-REALITY-003, and UI-REALITY-008 have current live receipts and no unresolved legacy-route telemetry is observed for the cutover window.",
    routes: ["/work", "/work/:path*", "/customers", "/classic"],
  },
] as const;

export function isDeprecatedOldProductPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  for (const prefix of DEPRECATED_OLD_PRODUCT_PREFIXES) {
    if (normalized === prefix) return true;
    if (normalized.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

export function resolveDeprecatedOldProductRedirect(
  pathname: string,
  search: string = "",
): string | null {
  if (!isDeprecatedOldProductPath(pathname)) return null;
  // Old-product `env` is not verification Demo/Real. Forward other query keys.
  return mergeQuery(CANONICAL_VERIFICATION_ROOT, search, ["env"]);
}

/** The only `/agents` pages still owned by the verification shell. */
export const CANONICAL_AGENTS_PAGE_ROOTS = ["/agents", "/agents/sessions", "/agents/qa"] as const;

export function isCanonicalAgentsPagePath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return CANONICAL_AGENTS_PAGE_ROOTS.some(
    (root) => normalized === root || (root !== "/agents" && normalized.startsWith(`${root}/`)),
  );
}

/**
 * Unmount leftover `/agents` ProductChrome onto the live dashboard.
 * `/agents/api` stays on the Vite→Hono proxy and the small canonical page
 * whitelist stays in the verification shell. Query is forwarded.
 */
export function resolveAgentsUnmountRedirect(pathname: string, search: string = ""): string | null {
  const normalized = normalizePathname(pathname);
  if (normalized === "/agents/api" || normalized.startsWith("/agents/api/")) {
    return null;
  }
  if (isCanonicalAgentsPagePath(normalized)) return null;
  if (normalized !== "/agents" && !normalized.startsWith("/agents/")) {
    return null;
  }
  return mergeQuery(CANONICAL_VERIFICATION_ROOT, search);
}

/** One redirect authority shared by the root and every retained child stub.
 * Ported from the CU-W15 trunk during the 2026-09-10 reconciliation: it only
 * composes resolvers this module already exported, so it adds an entry point
 * without changing any individual redirect's behaviour. */
export function resolveVerificationLegacyRedirect(
  pathname: string,
  search: string = "",
): string | null {
  return (
    resolveAgentsUnmountRedirect(pathname, search) ??
    resolveReviewVerdictEvidenceRedirect(pathname, search) ??
    resolveDeprecatedOldProductRedirect(pathname, search) ??
    resolveUnknownOldIdRedirect(pathname, search)
  );
}
